/*
 * ss-horizontal-scroll.js
 *
 * Reusable "weighty" scroll physics for any horizontal carousel on the site.
 * Replaces raw native horizontal scroll (which on macOS trackpad is 1:1 with
 * finger velocity — i.e. very fast) with a damped + lightly-inertial scroll
 * that feels calmer and more premium.
 *
 * Why:
 *   - macOS Chrome two-finger swipe delivers high-velocity wheel events.
 *   - With native scroll, each event moves scrollLeft by its full deltaX.
 *   - User feedback: "super sensitive", "not smooth".
 *   - Damping the per-event delta + decaying the accumulated velocity per
 *     frame gives the user a 1:1 responsive feel for small inputs and a
 *     short, satisfying glide for flicks.
 *
 * Usage:
 *   window.ssHorizontalScroll.wire(railElement)
 *
 *   Idempotent — multiple wires on the same element are deduped via a
 *   dataset flag.
 *
 * Scope:
 *   - Desktop only (>=769px). Mobile uses native touch scroll, which is
 *     already smooth via the platform's own momentum engine.
 *   - Only intercepts wheel events whose horizontal component dominates.
 *     Vertical-dominant wheel events (mouse wheel, vertical trackpad swipe)
 *     fall through so the page scrolls naturally.
 *   - Drag-to-scroll (pointer events) is independent — see ss-comfort-boxes.js
 *     for an example.
 */
(function(){
  if (window.ssHorizontalScroll) return;

  // Tuning constants — adjusted from user feedback. If a rail starts feeling
  // wrong, this is the place to retune.
  var INPUT_DAMP = 0.45;       // 0..1 — fraction of each wheel deltaX applied to velocity.
                               // Lower = less sensitive. 0.45 = 55% less sensitive than native.
  var FRICTION   = 0.82;       // 0..1 — fraction of velocity retained each frame.
                               // Lower = stops sooner. 0.82 ≈ ~12 frames glide (~200ms).
  var MAX_VEL    = 36;         // px per frame cap, prevents runaway from huge wheel deltas.
  var MIN_VEL    = 0.4;        // below this, treat as stopped.

  function isDesktop() {
    return window.matchMedia && window.matchMedia('(min-width: 769px)').matches;
  }

  function wire(rail) {
    if (!rail || rail.dataset.hScrollWired === '1') return;
    rail.dataset.hScrollWired = '1';

    if (!isDesktop()) return;

    var velocity = 0;
    var rafId = null;
    var lastTick = 0;
    // 60fps baseline frame budget. All tuning constants are expressed
    // relative to this so friction/velocity behave consistently across
    // 60Hz, 90Hz, 120Hz (ProMotion), 144Hz, etc.
    var BASE_FRAME_MS = 1000 / 60;

    function tick(now) {
      if (Math.abs(velocity) < MIN_VEL) {
        rafId = null;
        velocity = 0;
        lastTick = 0;
        return;
      }
      // Frame-rate-independent physics — `dt` is the ratio of this
      // frame's interval to the 60fps baseline. On a 120Hz ProMotion
      // display, dt ≈ 0.5 → each frame moves half the step but we get
      // twice as many frames per second, so the total glide duration
      // stays identical while the motion is genuinely smoother
      // (finer-grained intermediate positions).
      // First tick: dt = 1 (no prior frame to compare against).
      var dt = lastTick ? (now - lastTick) / BASE_FRAME_MS : 1;
      // Guard against tab-backgrounded huge dt that would lurch the rail
      // when the user returns. 4 = 4x 60Hz frame = ~67ms — generous.
      if (dt > 4) dt = 4;
      lastTick = now;

      // Step is per-60fps-frame velocity, scaled by actual elapsed time.
      var perFrameVel = Math.max(-MAX_VEL, Math.min(MAX_VEL, velocity));
      rail.scrollLeft += perFrameVel * dt;

      // Friction decays *per unit time*, not per frame. Math.pow with
      // dt gives the same effective decay rate at 60/90/120/144Hz.
      velocity *= Math.pow(FRICTION, dt);

      rafId = requestAnimationFrame(tick);
    }

    rail.addEventListener('wheel', function(e) {
      // Let vertical-dominant wheel events through — they're the user
      // trying to scroll the page, not the rail.
      var absX = Math.abs(e.deltaX);
      var absY = Math.abs(e.deltaY);
      if (absX < 0.5 && absY > absX) return;

      // We're going to handle this scroll ourselves. preventDefault stops
      // the native scroll AND stops the browser bubbling to page scroll.
      e.preventDefault();

      // Use deltaX if present, fall back to deltaY (lets shift+wheel and
      // vertical mouse-wheel on a hovered rail scroll horizontally).
      var dx = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      velocity += dx * INPUT_DAMP;

      // Hard cap to absorb extreme inertia spikes (some Logitech mice
      // can fire deltaY = 1000+ in a single event).
      if (velocity >  MAX_VEL * 4) velocity =  MAX_VEL * 4;
      if (velocity < -MAX_VEL * 4) velocity = -MAX_VEL * 4;

      if (!rafId) rafId = requestAnimationFrame(tick);
    }, { passive: false });

    // If the user starts a pointer-drag (see ss-comfort-boxes.js drag wiring),
    // kill any in-flight inertia so the rail responds instantly to the drag.
    rail.addEventListener('pointerdown', function(e){
      if (e.pointerType !== 'mouse') return;
      velocity = 0;
      lastTick = 0;
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    });
  }

  window.ssHorizontalScroll = { wire: wire };
})();
